// =============================================================================
// Event detail view — opens when an event in the day list or the activity
// feed is tapped. Shows the event in its label color with participant
// avatars, date/time, an activity log, and chat-style comments; edit and
// delete are reachable from the header. Looks the event up across every
// calendar the user can see and closes itself if it was deleted meanwhile.
// =============================================================================

import React from 'react';
import { ArrowLeft, Pencil, Trash2, ChevronRight, Send } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { formatDate as fmt } from '../../lib/dates';
import { useTranslation } from '../../lib/i18n';
import { useConfirm } from '../../lib/ConfirmDialog';
import { useAuth } from '../../lib/AuthContext';

const EventDetail = ({
  eventId,
  occurrenceDate,
  events,
  getCategory,
  membersById,
  renderCreatorAvatar,
  onClose,
  onEdit,
  onRequestRecurringDelete,
  commitAllEvents,
  commentDraft,
  setCommentDraft,
  onAddComment
}) => {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const { user } = useAuth();

  // Look up across every event the user can see (not just the current
  // calendar) so the activity feed can open events from any shared
  // calendar without auto-closing.
  const ev = events.find((e) => e.id === eventId);
  if (!ev) {
    // Event was deleted while open — auto-close.
    setTimeout(() => onClose(), 0);
    return null;
  }
  const cat = getCategory(ev.categoryId);
  const color = cat?.color || '#64748b';
  const ids =
    Array.isArray(ev.participantIds) && ev.participantIds.length > 0
      ? ev.participantIds
      : ev.createdBy
      ? [ev.createdBy]
      : [];
  const fmtDate = (iso) =>
    fmt(parseISO(iso), 'weekdayFull');
  const fmtTime = (hhmm) => hhmm || '';
  const sameDay = (ev.endDate || ev.date) === ev.date;

  // Merge audit + comments into one chronological timeline.
  const audit = (ev.audit || []).map((a) => ({ ...a, kind: 'audit' }));
  const comments = (ev.comments || []).map((c) => ({
    ...c,
    kind: 'comment'
  }));
  const timeline = [...audit, ...comments].sort((a, b) =>
    (a.at || '').localeCompare(b.at || '')
  );

  const fieldLabel = (f) => {
    switch (f) {
      case 'title':
        return t('common.title');
      case 'date':
        return t('common.date');
      case 'label':
        return t('calendar.fieldLabel');
      case 'notes':
        return t('common.notes');
      case 'participants':
        return t('sharedCalendar.participants');
      case 'recurrence':
        return t('calendar.recurrence');
      default:
        return f;
    }
  };
  const auditSummary = (a) => {
    if (a.type === 'created') return t('calendar.auditCreated');
    const fields = a.fields || [];
    if (fields.length === 0) return t('calendar.auditUpdated');
    return t('calendar.auditFieldsUpdated', { fields: fields.map(fieldLabel).join(', ') });
  };
  const fmtRelative = (iso) => {
    try {
      const d = parseISO(iso);
      const today = new Date();
      const diffDays = Math.floor(
        (today.setHours(0, 0, 0, 0) - new Date(d).setHours(0, 0, 0, 0)) /
          86400000
      );
      if (diffDays === 0) return format(d, 'HH:mm');
      if (diffDays === 1) return t('common.yesterday', 'Gestern');
      return fmt(d, 'medium');
    } catch {
      return iso;
    }
  };

  return (
    <div
      className="fixed inset-0 z-[55] bg-black/40 flex items-center justify-center sm:p-4"
      style={{ height: '100dvh' }}
      onClick={() => onClose()}
    >
      <div
        className="bg-white dark:bg-gray-900 w-full sm:max-w-lg sm:rounded-2xl shadow-2xl flex flex-col sm:h-auto sm:max-h-[90vh] overflow-hidden"
        style={{ height: '100dvh' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header (label-tinted). The sheet is full-height, so the
            toolbar has to start below the status bar or the back /
            edit / delete buttons sit under the clock and never get
            the tap. The tint still runs up behind the status bar. */}
        <div
          className="px-4 pb-5"
          style={{
            backgroundColor: `${color}1a`,
            paddingTop: 'calc(env(safe-area-inset-top, 0px) + 1rem)'
          }}
        >
          <div className="flex items-center justify-between">
            <button
              onClick={() => onClose()}
              className="p-1.5 -ml-1 rounded-full hover:bg-black/5 dark:hover:bg-white/5"
              aria-label={t('common.back')}
              style={{ color }}
            >
              <ArrowLeft size={22} />
            </button>
            <div className="flex items-center gap-1">
              <button
                onClick={() => {
                  onClose();
                  onEdit(ev, occurrenceDate);
                }}
                className="p-1.5 rounded-full hover:bg-black/5 dark:hover:bg-white/5"
                aria-label={t('common.edit')}
                style={{ color }}
              >
                <Pencil size={20} />
              </button>
              <button
                onClick={async () => {
                  // Recurring events get the 3-option dialog (this
                  // occurrence / this and future / whole series) —
                  // a plain confirm here silently deleted the whole
                  // series including past occurrences.
                  if (ev.recurrence && ev.recurrence.freq) {
                    onRequestRecurringDelete({
                      baseId: ev.id,
                      occurrenceDate: occurrenceDate || ev.date
                    });
                    onClose();
                    return;
                  }
                  if (
                    await confirm({
                      message: t('calendar.deleteEventConfirm'),
                      danger: true
                    })
                  ) {
                    commitAllEvents(events.filter((e) => e.id !== ev.id));
                    onClose();
                  }
                }}
                className="p-1.5 rounded-full hover:bg-black/5 dark:hover:bg-white/5"
                aria-label={t('common.delete')}
                style={{ color }}
              >
                <Trash2 size={20} />
              </button>
            </div>
          </div>

          {/* Avatars */}
          {ids.length > 0 && (
            <div className="mt-2 flex items-center justify-center -space-x-2">
              {ids.slice(0, 6).map((uid) => (
                <div
                  key={uid}
                  className="ring-2 rounded-full"
                  style={{ ['--tw-ring-color']: `${color}33` }}
                >
                  {renderCreatorAvatar(uid, 36)}
                </div>
              ))}
            </div>
          )}

          {/* Title */}
          <h2
            className="mt-3 text-center text-2xl font-bold leading-tight"
            style={{ color }}
          >
            {ev.title}
          </h2>

          {/* Date / time */}
          <div className="mt-3 flex items-center justify-center gap-3 text-sm">
            <div className="text-center">
              <div className="font-semibold text-gray-800 dark:text-gray-100">
                {fmtDate(ev.date)}
              </div>
              {!ev.allDay && ev.startTime && (
                <div
                  className="text-2xl font-bold leading-none mt-0.5"
                  style={{ color }}
                >
                  {fmtTime(ev.startTime)}
                </div>
              )}
            </div>
            <ChevronRight size={20} style={{ color }} />
            <div className="text-center">
              <div className="font-semibold text-gray-800 dark:text-gray-100">
                {fmtDate(ev.endDate || ev.date)}
              </div>
              {!ev.allDay && ev.endTime && (
                <div
                  className="text-2xl font-bold leading-none mt-0.5"
                  style={{ color }}
                >
                  {fmtTime(ev.endTime)}
                </div>
              )}
              {ev.allDay && sameDay && (
                <div className="text-xs text-gray-500 mt-0.5">{t('calendar.allDay')}</div>
              )}
            </div>
          </div>

          {ev.notes && (
            <p className="mt-3 text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap text-center">
              {ev.notes}
            </p>
          )}
        </div>

        {/* Activity timeline */}
        <div className="flex-1 overflow-y-auto px-4 py-4 bg-white dark:bg-gray-900">
          {timeline.length === 0 ? (
            <div className="text-center text-xs text-gray-400 dark:text-gray-500 py-6">
              {t('calendar.noActivity')}
            </div>
          ) : (
            <div className="space-y-3">
              {timeline.map((item, idx) => {
                const prev = timeline[idx - 1];
                const showDateHeader =
                  !prev ||
                  format(parseISO(item.at), 'yyyy-MM-dd') !==
                    format(parseISO(prev.at), 'yyyy-MM-dd');
                const m = membersById[item.userId];
                const name = m?.displayName || t('common.unknown');
                const isMe = user && item.userId === user.id;
                return (
                  <div key={item.id}>
                    {showDateHeader && (
                      <div className="flex items-center gap-2 my-3 text-[11px] uppercase tracking-wide text-gray-400">
                        <div className="flex-1 border-t border-gray-200 dark:border-gray-800" />
                        <span>{fmtRelative(item.at)}</span>
                        <div className="flex-1 border-t border-gray-200 dark:border-gray-800" />
                      </div>
                    )}

                    {item.kind === 'audit' ? (
                      <div className="flex items-center justify-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                        {renderCreatorAvatar(item.userId, 18)}
                        <span>{auditSummary(item)}</span>
                      </div>
                    ) : (
                      <div
                        className={`flex items-end gap-2 ${
                          isMe ? 'flex-row-reverse' : ''
                        }`}
                      >
                        {!isMe && renderCreatorAvatar(item.userId, 28)}
                        <div
                          className={`max-w-[75%] rounded-2xl px-3 py-2 text-sm ${
                            isMe
                              ? 'bg-primary-600 text-white'
                              : 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100'
                          }`}
                          style={
                            isMe ? { backgroundColor: color } : undefined
                          }
                        >
                          {!isMe && (
                            <div className="text-[11px] font-semibold opacity-70 mb-0.5">
                              {name}
                            </div>
                          )}
                          <div className="whitespace-pre-wrap break-words">
                            {item.text}
                          </div>
                          <div
                            className={`text-[10px] mt-1 ${
                              isMe ? 'text-white/70' : 'text-gray-500'
                            }`}
                          >
                            {format(parseISO(item.at), 'HH:mm')}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Comment input */}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            onAddComment();
          }}
          className="border-t border-gray-200 dark:border-gray-800 px-2 pt-2 flex items-center gap-2 bg-white dark:bg-gray-900"
          style={{
            paddingBottom:
              'max(env(safe-area-inset-bottom), 0.5rem)'
          }}
        >
          <input
            type="text"
            value={commentDraft}
            onChange={(e) => setCommentDraft(e.target.value)}
            placeholder={t('calendar.commentPlaceholder')}
            className="flex-1 bg-gray-100 dark:bg-gray-800 rounded-full px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
          <button
            type="submit"
            disabled={!commentDraft.trim()}
            className="p-2 rounded-full text-white disabled:opacity-40"
            style={{ backgroundColor: color }}
            aria-label={t('calendar.send')}
          >
            <Send size={18} />
          </button>
        </form>
      </div>
    </div>
  );
};

export default EventDetail;
